import { supabase } from '@/lib/supabase';

export type TimeRange = '24h' | '7d' | '30d';

export type CompanyLite = { id: string; name: string };

export type CallRecord = {
  id: string;
  created_at: string;
  status: string;
  duration?: number | null;
  transferred_to_agent?: boolean | null;
  company_configs?: { name?: string | null } | null;
  [key: string]: any;
};

export type AiMetricRecord = {
  id?: string;
  created_at: string;
  metric_type: 'booking_rate' | 'time_to_book_seconds' | 'objection_count' | 'tool_latency_ms' | string;
  metric_value: number;
  company_configs?: { name?: string | null } | null;
  [key: string]: any;
};

export type TrendSeries = {
  bookingRate: number[];
  timeToBook: number[];
  objections: number[];
  toolLatency: number[];
  transferRate: number[];
};

export function getDateRange(timeRange: TimeRange): { startDate: Date; endDate: Date } {
  const endDate = new Date();
  const startDate = new Date(endDate.getTime());
  switch (timeRange) {
    case '24h':
      startDate.setHours(endDate.getHours() - 24);
      break;
    case '7d':
      startDate.setDate(endDate.getDate() - 7);
      break;
    case '30d':
      startDate.setDate(endDate.getDate() - 30);
      break;
  }
  return { startDate, endDate };
}

export async function loadActiveCompanies(): Promise<CompanyLite[]> {
  const { data, error } = await supabase
    .from('company_configs')
    .select('id, name')
    .eq('is_active', true)
    .order('name');

  if (error) throw error;
  return (data || []) as CompanyLite[];
}

export async function fetchRecentCalls(params: {
  timeRange: TimeRange;
  companyId: string | 'all';
  limit?: number;
}): Promise<CallRecord[]> {
  const { timeRange, companyId, limit = 50 } = params;
  const { startDate } = getDateRange(timeRange);

  let q = supabase
    .from('ai_voice_calls')
    .select(
      `
      *,
      company_configs(name)
    `
    )
    .gte('created_at', startDate.toISOString())
    .order('created_at', { ascending: false })
    .limit(limit);

  if (companyId !== 'all') {
    q = q.eq('company_config_id', companyId);
  }

  const { data, error } = await q;
  if (error) throw error;
  return (data || []) as CallRecord[];
}

export async function fetchAiMetrics(params: {
  timeRange: TimeRange;
  companyId: string | 'all';
  limit?: number;
}): Promise<AiMetricRecord[]> {
  const { timeRange, companyId, limit = 100 } = params;
  const { startDate } = getDateRange(timeRange);

  let q = supabase
    .from('ai_call_metrics')
    .select(
      `
      *,
      company_configs(name)
    `
    )
    .gte('created_at', startDate.toISOString())
    .order('created_at', { ascending: false })
    .limit(limit);

  if (companyId !== 'all') {
    q = q.eq('company_id', companyId);
  }

  const { data, error } = await q;
  if (error) throw error;
  return (data || []) as AiMetricRecord[];
}

export function computeSummaryMetrics(calls: CallRecord[]): {
  total_calls: number;
  successful_calls: number;
  avg_duration: number;
  transfer_rate: number;
  satisfaction_score: number;
} {
  const totalCalls = calls.length;
  const successfulCalls = calls.filter((c) => c.status === 'completed').length;
  const avgDuration =
    totalCalls > 0 ? calls.reduce((sum, c) => sum + (c.duration || 0), 0) / totalCalls : 0;
  const transferredCalls = calls.filter((c) => !!c.transferred_to_agent).length;
  const transferRate = totalCalls > 0 ? (transferredCalls / totalCalls) * 100 : 0;

  return {
    total_calls: totalCalls,
    successful_calls: successfulCalls,
    avg_duration: Math.round(avgDuration),
    transfer_rate: Math.round(transferRate),
    // Placeholder until explicit satisfaction source is wired
    satisfaction_score: 4.2,
  };
}

function safeAvg(sum: number, n: number) {
  return n > 0 ? sum / n : 0;
}

export function computeTrends(args: {
  calls: CallRecord[];
  aiMetrics: AiMetricRecord[];
  timeRange: TimeRange;
}): TrendSeries {
  const { calls, aiMetrics, timeRange } = args;

  const now = new Date();
  const { startDate } = getDateRange(timeRange);
  const end = now.getTime();
  const start = startDate.getTime();
  const hourMs = 60 * 60 * 1000;
  const dayMs = 24 * hourMs;
  const bucketMs = timeRange === '24h' ? hourMs : dayMs;
  const bucketCount = Math.max(2, Math.ceil((end - start) / bucketMs));
  const init = (n: number) => Array.from({ length: n }, () => 0);
  const counts = (n: number) => Array.from({ length: n }, () => 0);

  const booking = init(bucketCount);
  const bookingN = counts(bucketCount);
  const ttb = init(bucketCount);
  const ttbN = counts(bucketCount);
  const objections = init(bucketCount);
  const objN = counts(bucketCount);
  const latency = init(bucketCount);
  const latN = counts(bucketCount);
  const transfer = init(bucketCount);
  const transferDen = counts(bucketCount);

  const idxFor = (ts: number) => {
    const i = Math.floor((ts - start) / bucketMs);
    return Math.min(Math.max(i, 0), bucketCount - 1);
  };

  // Aggregate ai_call_metrics per bucket
  (aiMetrics || []).forEach((m) => {
    const ts = new Date(m.created_at).getTime();
    const i = idxFor(ts);
    const val = Number(m.metric_value) || 0;
    switch (m.metric_type) {
      case 'booking_rate':
        booking[i] += val;
        bookingN[i]++;
        break;
      case 'time_to_book_seconds':
        ttb[i] += val;
        ttbN[i]++;
        break;
      case 'objection_count':
        objections[i] += val;
        objN[i]++;
        break;
      case 'tool_latency_ms':
        latency[i] += val;
        latN[i]++;
        break;
      default:
        break;
    }
  });

  // Compute transfer rate per bucket from ai_voice_calls
  (calls || []).forEach((c) => {
    const ts = new Date(c.created_at).getTime();
    const i = idxFor(ts);
    transferDen[i]++;
    if (c.transferred_to_agent) transfer[i]++;
  });

  const trendBooking = booking.map((s, i) => Math.round(safeAvg(s, bookingN[i])));
  const trendTtb = ttb.map((s, i) => safeAvg(s, ttbN[i]));
  const trendObj = objections.map((s, i) => safeAvg(s, objN[i]));
  const trendLat = latency.map((s, i) => safeAvg(s, latN[i]));
  const trendTransfer = transfer.map((s, i) => {
    const denom = transferDen[i] || 0;
    return denom > 0 ? Math.round((s / denom) * 100) : 0;
  });

  return {
    bookingRate: trendBooking,
    timeToBook: trendTtb,
    objections: trendObj,
    toolLatency: trendLat,
    transferRate: trendTransfer,
  };
}
